/**
 * The two-column frame every auth screen sits in.
 *
 * Read off `design/screens/auth-signup.html`: an equal two-column grid, the
 * form on white at `--mdx-space-72 --mdx-space-56`, its contents capped at
 * `--mdx-auth-column` with a `--mdx-space-28` stack; the right column on
 * `--mdx-ground` behind a left `--mdx-border`, vertically centred.
 *
 * Under 1024px the two columns STACK — form first, reassurance panel beneath it
 * behind a top border instead of a left one. That is what the mockups' own 375px
 * captures show, and the screenshot pass is how it was caught: the first build
 * hid the panel on the strength of an assumption about what phones want, and the
 * capture disagreed. ARCH §7.5: two widths only, and the switch is a component
 * decision rather than a breakpoint cascade.
 *
 * Both stacked columns sit inside the mockups' 20px side gutter — every auth
 * capture draws it. It was 24px, an undrawn value no comparison listed, until
 * Story 24.2's recovery step wrapped its label row at 375px where the drawing
 * fits it on one line. The vertical padding stays 48px against the drawn 40/36
 * and 32/40: 36px names no token (the census counts it untokenised), so that
 * difference is listed rather than invented.
 *
 * Story 3.3.
 */
import type { ReactNode } from 'react'
import { Wordmark } from '../ui/Wordmark'

export function AuthPanel({
  children,
  aside,
  /**
   * `auth-signup` opens its form column with the wordmark; `auth-login` does
   * not — its wordmark lives in the right panel above "Welcome back." Checked
   * against the markup rather than assumed, after the first build put one on
   * both.
   */
  wordmark = true,
  /**
   * The form column's stack gap. Not one value across the screens: `auth-signup`
   * builds on 28px and `auth-login` on 24px, and the difference is real — sign-up
   * carries two paragraphs of legal copy that need the air, login carries two
   * fields that do not.
   */
  stack = 'gap-mdx-28',
}: {
  children: ReactNode
  aside?: ReactNode
  wordmark?: boolean
  stack?: 'gap-mdx-24' | 'gap-mdx-28'
}) {
  return (
    <main className="grid min-h-screen grid-cols-1 items-stretch bg-mdx-white lg:grid-cols-2">
      <div className="flex min-w-0 justify-center px-mdx-20 py-mdx-48 lg:px-mdx-56 lg:py-mdx-72">
        <div className={`flex w-full min-w-0 max-w-mdx-auth-column flex-col ${stack}`}>
          {wordmark ? <Wordmark /> : null}
          {children}
        </div>
      </div>
      {aside ? (
        <aside className="flex min-w-0 items-center border-t border-mdx-line bg-mdx-ground px-mdx-20 py-mdx-48 lg:border-l lg:border-t-0 lg:px-mdx-56 lg:py-mdx-72">
          <div className="flex max-w-mdx-auth-column flex-col gap-mdx-22">{aside}</div>
        </aside>
      ) : null}
    </main>
  )
}

/**
 * The heading pair — a verdict sentence, then one line under it.
 *
 * `auth-signup.html`'s note: "the heading is a verdict sentence, not a pitch."
 * `--mdx-text-verdict` is the same token the Verdict block uses, which is the
 * point: the product's signature voice starts before anyone has signed up.
 */
export function AuthHeading({
  title,
  children,
  note,
}: {
  title: string
  children?: ReactNode
  /**
   * The quieter line a step heading carries instead — 15px `--mdx-slate` at
   * `--mdx-measure-48`, read off `auth-login.html`'s code and recovery steps,
   * where it says where the code comes from rather than making a pitch.
   */
  note?: ReactNode
}) {
  return (
    <div className="flex flex-col gap-mdx-10">
      <h1 className="m-0 text-mdx-verdict font-mdx-bold leading-mdx-verdict tracking-mdx-verdict text-balance">
        {title}
      </h1>
      {children ? (
        <p className="m-0 max-w-mdx-measure-52 text-mdx-18 leading-mdx-reason text-mdx-slate">{children}</p>
      ) : null}
      {note ? <p className="m-0 max-w-mdx-measure-48 text-pretty text-mdx-15 text-mdx-slate">{note}</p> : null}
    </div>
  )
}

/** A ticked reassurance line for the right column. */
export function AuthPoint({ children }: { children: ReactNode }) {
  return (
    <span className="flex items-start gap-mdx-14">
      <span
        aria-hidden="true"
        className="mt-mdx-4 flex size-mdx-20 shrink-0 items-center justify-center rounded-mdx-pill bg-mdx-hot"
      >
        <span className="block h-mdx-4 w-mdx-8 -translate-y-px translate-x-px -rotate-45 border-b-2 border-l-2 border-mdx-white" />
      </span>
      <span className="max-w-mdx-measure-34 text-mdx-17 font-mdx-semibold tracking-mdx-h3">{children}</span>
    </span>
  )
}

/** The eyebrow above a group — 12px 700 uppercase `.1em` slate. */
export function AuthEyebrow({ children }: { children: ReactNode }) {
  return (
    <span className="text-mdx-12 font-mdx-bold uppercase tracking-mdx-eyebrow text-mdx-slate">
      {children}
    </span>
  )
}

/** The secondary line under a form — "Already have an account? Log in". */
export function AuthFootnote({ children }: { children: ReactNode }) {
  return <p className="m-0 text-mdx-15 text-mdx-slate">{children}</p>
}

/**
 * An inline link in `--mdx-brand-deep`, 600 — the mockups' only link style.
 *
 * UNDERLINED by default. Every auth mockup leaves the browser default in place,
 * and inside a paragraph of slate body text a link that is only distinguished by
 * colour is one a colour-blind reader cannot find. The wordmark is the one
 * exception, and it opts out for itself.
 *
 * `external` opens the link in a new tab, and Story 3.10 added it for one
 * reason: the Terms and Privacy links on the signup form now point at the
 * MARKETING SITE, a different host, and following them in place would discard
 * a half-filled signup form. Every other `AuthLink` goes to another route of
 * this same app — "Log in", "Reset your password" — where a new tab would be
 * wrong. `rel` is set explicitly rather than left to the browser's implicit
 * `noopener`, because `noreferrer` is not implied by anything.
 */
export function AuthLink({
  href,
  external = false,
  children,
}: {
  href: string
  external?: boolean
  children: ReactNode
}) {
  return (
    <a
      href={href}
      className="font-mdx-semibold text-mdx-brand-deep underline"
      {...(external ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
    >
      {children}
    </a>
  )
}

/**
 * An action on the SAME screen, drawn as the link it looks like — a `<button>`,
 * because it changes what the form asks for rather than going anywhere.
 *
 * `auth-login.html`'s second step has three: *Use a recovery code instead* and
 * *Use your authenticator app instead* on the field's label line, in the style
 * of *Forgot your password?* (`small`), and *Use a different account* in the
 * footnote, in `AuthLink`'s. An `<a href="#">` would announce a navigation that
 * never happens.
 */
export function AuthAction({
  onClick,
  small = false,
  children,
}: {
  onClick: () => void
  small?: boolean
  children: ReactNode
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={[
        'cursor-pointer border-0 bg-transparent p-0 text-left font-mdx-semibold text-mdx-brand-deep underline',
        small ? 'rounded-mdx-sm text-mdx-13' : '',
      ].join(' ')}
    >
      {children}
    </button>
  )
}

/**
 * The OTHER auth layout — a single centred card on `--mdx-ground`.
 *
 * `auth-reset.html` and `auth-verify.html` are not the two-column split that
 * `auth-signup` and `auth-login` use. They are a wordmark above a card at
 * `--mdx-auth-card`, and the difference is not decorative: sign-up and log-in
 * are doorways with something to say beside them, while reset and verify are a
 * single instruction handed to somebody mid-flow. There is nothing to put in a
 * second column, and a half-empty one would say there should be.
 *
 * Built after the screenshot comparison caught both of them rendered as the
 * two-column layout — the mockups' markup has no grid at all.
 *
 * The page frame is ALWAYS centred — DECISION-039. `auth-reset.html` looks like
 * it left-aligns, and it never renders the case: its captures are a three-state
 * comparison, side by side at 1440px and stacked at 375px, so a single card is
 * never alone on the page and the container describes the comparison rather
 * than the screen. `auth-verify.html` and `auth-invite.html` do render it, and
 * their containers are identical to the byte.
 *
 * Stories 3.4, 3.9.
 */
export function AuthCard({
  children,
  align = 'centre',
  gap = 'gap-mdx-16',
  pad = 'p-mdx-28',
}: {
  children: ReactNode
  /**
   * The CARD's contents — a separate question from where the card sits, which
   * is what the old `centred` prop got wrong by answering both at once. verify,
   * invite and reset's sent and expired states centre; reset's two form states
   * do not, because a label centred over its own input is harder to read and
   * the mockup leaves them left.
   */
  align?: 'centre' | 'left'
  /** verify 16 · reset's forms 20 · invite's forms 24 · the centred cards 14. */
  gap?: 'gap-mdx-14' | 'gap-mdx-16' | 'gap-mdx-20' | 'gap-mdx-24'
  /** the expired card alone is `48px 28px` — `--mdx-space-48` is named for it. */
  pad?: 'p-mdx-28' | 'px-mdx-28 py-mdx-48'
}) {
  return (
    <main className="flex min-h-screen flex-col items-center gap-mdx-28 bg-mdx-ground px-mdx-32 pb-mdx-72 pt-mdx-64">
      <Wordmark />
      <div
        className={[
          'flex w-full max-w-mdx-auth-card flex-col',
          gap,
          'rounded-mdx-card border border-mdx-line bg-mdx-white shadow-mdx-card',
          pad,
          align === 'centre' ? 'items-center text-center' : '',
        ].join(' ')}
      >
        {children}
      </div>
    </main>
  )
}

/**
 * The card's own heading — an H2 at `--mdx-text-25`, not the H1 the two-column
 * screens use. A card is a thing on a page, and the page's title is the
 * wordmark above it.
 */
export function CardHeading({ title, children }: { title: string; children?: ReactNode }) {
  return (
    <>
      <h1 className="m-0 text-mdx-25 font-mdx-bold leading-mdx-heading tracking-mdx-h1 text-balance">
        {title}
      </h1>
      {children ? (
        <p className="m-0 max-w-mdx-measure-44 text-mdx-16 leading-mdx-body">{children}</p>
      ) : null}
    </>
  )
}

/**
 * The 40px square a card leads with — `--mdx-space-40`, radius 10px.
 *
 * Three tones, all read off the mockups: `--mdx-brand` on `auth-verify`,
 * `--mdx-error` on `auth-invite`'s expired card, `--mdx-hot` on its accepted
 * one. The tone is what the card MEANS, so it is a prop rather than three
 * near-identical components. Story 3.9.
 */
export function CardMark({
  children,
  tone = 'brand',
}: {
  children: ReactNode
  tone?: 'brand' | 'error' | 'hot'
}) {
  const background =
    tone === 'error' ? 'bg-mdx-error' : tone === 'hot' ? 'bg-mdx-hot' : 'bg-mdx-brand'
  return (
    <span
      aria-hidden="true"
      className={`flex size-mdx-40 shrink-0 items-center justify-center rounded-mdx-control text-mdx-white ${background}`}
    >
      {children}
    </span>
  )
}
